import { HeaderButtonIdentifier } from '@/components/elements/HeaderButton/HeaderButton.types';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import CheckoutViewLayout from '@/components/layouts/CheckoutViewLayout';
import CardWrapper from '@/components/modules/CardWrapper';
import BookingInfo, { CheckoutDetailsProps } from '@/components/modules/checkout/CheckoutDetails/CheckoutDetails';
import KlarnaCheckout from '@/components/modules/checkout/KlarnaCheckout/KlarnaCheckout';
import GoBack from '@/components/modules/pages/bokningar/GoBack';
import SEO, { seoPropsFromBaseString } from '@/components/modules/SEO';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { isSistaminuten, url } from '@/helpers';
import {
  getBookingCheckoutEmployee,
  getBookingCheckoutSummary,
  mapBookingCheckoutToCheckoutDetailsProps,
  mapBookStateToCheckoutDetailsProducts,
  mapBookStateToCheckoutDetailsServices,
} from '@/helpers/checkout';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import withPlaceDetailsFallback from '@/hoc/withPlaceDetailsFallback';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale/i18n';
import { InitKlarnaResponse, initKlarnaResponseSchema } from '@/types/api/services/booking';
import { BookState, bookStateSchema } from '@/types/state/book';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useKlarnaBookingCheckoutManager from './KlarnaBookingCheckout.hooks';

const baseTranslationKey = 'pages.booking.checkout.KlarnaBookingCheckout' as const;

const KlarnaBookingCheckout = ({ booking, klarnaOrder }: { booking: BookState; klarnaOrder: InitKlarnaResponse }) => {
  const location = useLocation<{ savedCheckoutState?: unknown }>();
  const history = useHistory();
  const employee = getBookingCheckoutEmployee(location.state, booking);
  const selectedCapacity = booking.time?.selectedCapacity || 1;
  const summary = getBookingCheckoutSummary({ booking, employee, selectedCapacity });
  const { klarnaBookingCheckoutAPI, status } = useKlarnaBookingCheckoutManager({
    employeeId: employee.actual.id,
    klarnaOrder,
  });
  const { isMobileView } = useMobileView();

  const services = mapBookStateToCheckoutDetailsServices(booking, employee.shown);
  const products = mapBookStateToCheckoutDetailsProducts(booking);
  const { giftcards, ...rest } = mapBookingCheckoutToCheckoutDetailsProps(
    summary,
    { type: CHECKOUT_PAYMENT_METHOD.KLARNA },
    employee.shown,
    booking,
  );

  const bookingInfoProps: CheckoutDetailsProps = {
    ...rest,
    services,
    products,
    giftcards: giftcards.map((giftcard) => ({
      ...giftcard,
    })),
  };
  const { logo } = bookingInfoProps;

  const handleOnBackButtonClick = () => {
    handlePageLeave();
    history.replace({
      pathname: '/booking/checkout',
      state: { savedCheckoutState: location.state?.savedCheckoutState, employee: employee.actual },
    });
  };

  const handlePageLeave = () => {
    bookingFlowTrackingSession.checkoutPaymentMethod('cancel', 'klarna');
  };

  /**
   * listen to history changes to detect when user navigates back
   */
  useEffect(() => {
    const unlisten = history.listen((_location, action) => {
      if (action === 'POP') {
        handlePageLeave();
      }
    });
    return unlisten;
  }, [history]);

  return (
    <CheckoutViewLayout
      header={{
        leftSlot: {
          button: {
            item: {
              identifier: HeaderButtonIdentifier.Back,
              icon: 'back',
            },
            'aria-label': _t('back'),
            onClick: handleOnBackButtonClick,
          },
          text: _t('back'),
        },
        middleSlot: {
          title: _t(`${baseTranslationKey}.page-title`),
          subtitle: bookingInfoProps.placeName,
        },
        rightSlot: {
          ...(logo && { imgUrl: logo }),
        },
      }}
      wrapperClass={isMobileView ? 'bg-brown-50' : 'bg-gradient'}>
      {status === 'SUBMITTING' && <LoadingPlaceHolder className="fixed h-screen" />}
      <div className="bg-brown-50 lg:bg-transparent">
        <div className="lg:py-xxl lg:container">
          <div className="gap-xxl flex items-start">
            <div className="gap-lg flex grow-[9999] basis-[600px] flex-col">
              <CardWrapper className="w-full lg:hidden">
                <BookingInfo {...bookingInfoProps} />
              </CardWrapper>
              <CardWrapper>
                <div className="p-lg">
                  <KlarnaCheckout
                    clientToken={klarnaOrder.clientToken}
                    orderLines={klarnaOrder.orderLines}
                    handleCreateOrder={klarnaBookingCheckoutAPI().onSubmitKlarna}
                  />
                </div>
              </CardWrapper>
            </div>
            <div className="sticky top-20 hidden lg:flex lg:grow-[100] lg:basis-[390px]">
              <CardWrapper className="w-full">
                <BookingInfo key={2} {...bookingInfoProps} />
              </CardWrapper>
            </div>
          </div>
        </div>
      </div>

      <SEO
        {...seoPropsFromBaseString('klarna', `${url.getBaseUrl()}booking/checkout/klarna`)}
        noindex={isSistaminuten()}
        image={url.getBaseImageUrl()}
      />
    </CheckoutViewLayout>
  );
};

export default withPlaceDetailsFallback(() => {
  const location = useLocation<{ klarnaOrder?: unknown }>();
  const bookState = useAppSelector((state) => state.book);
  const parsedBookState = bookStateSchema.safeParse(bookState);
  const parsedKlarnaOrder = initKlarnaResponseSchema.safeParse(location?.state?.klarnaOrder);

  if (!parsedBookState.success) return <GoBack />;

  const { saved, services, time, place } = parsedBookState.data;
  const invalidState =
    !parsedKlarnaOrder.success || saved || !services || !services.length || !place || !time || !time.timestamp;

  if (invalidState) return <GoBack />;

  return <KlarnaBookingCheckout booking={parsedBookState.data} klarnaOrder={parsedKlarnaOrder.data} />;
});
